import { useState } from "react";
import { promoDiscount, useActivePromotion } from "@/hooks/useActivePromotion";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Lock, CreditCard, AlertTriangle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SquareCardForm from "@/components/SquareCardForm";
import LoyaltyPanel from "@/components/LoyaltyPanel";
import FreeDrinkPicker from "@/components/FreeDrinkPicker";
import TipPicker from "@/components/TipPicker";
import SquareWalletButtons from "@/components/SquareWalletButtons";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { T } from "@/context/TextEditContext";

const TIME_SLOTS = ["ASAP (10-15 min)"].concat(
  Array.from({ length: 14 }, (_, i) => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 20 + i * 15);
    const m = now.getMinutes();
    const h = now.getHours();
    const hh = ((h + 11) % 12) + 1;
    const suffix = h >= 12 ? "PM" : "AM";
    return `${hh}:${m.toString().padStart(2, "0")} ${suffix}`;
  })
);

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [name, setName] = useState(() => localStorage.getItem("bk_loyalty_name") || "");
  const [phone, setPhone] = useState(() => localStorage.getItem("bk_loyalty_phone") || "");
  const [slot, setSlot] = useState(TIME_SLOTS[0]);
  const [notes, setNotes] = useState("");
  const [showAllergy, setShowAllergy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cardApi, setCardApi] = useState(null);
  const [cardError, setCardError] = useState(null);
  const [reward, setReward] = useState(null); // { key, choice }
  const [freeDrink, setFreeDrink] = useState(null); // drink name
  const [tip, setTip] = useState(0); // dollars
  const [pickupMethod, setPickupMethod] = useState("truck"); // "truck" | "curbside"
  const [vehicle, setVehicle] = useState("");
  const [declineError, setDeclineError] = useState(null); // full Square decline message (persistent)
  const { settings } = useSiteSettings();

  // Single editable sales-tax rate from Site Settings (Admin → Site tab).
  const salesTaxRate = Number(settings?.sales_tax_rate ?? 0.105);
  const promo = useActivePromotion("online");
  const promoOff = promoDiscount(subtotal, promo);
  const rewardDiscount = Math.min(Math.round((subtotal - promoOff) * 100) / 100, Number(reward?.price || 0));
  const netSubtotal = Math.max(0, Math.round((subtotal - promoOff - rewardDiscount) * 100) / 100);
  const tax = Math.round(netSubtotal * salesTaxRate * 100) / 100;
  const tipAmount = Math.max(0, Math.round(Number(tip || 0) * 100) / 100);
  const total = Math.round((netSubtotal + tax + tipAmount) * 100) / 100;

  // Pretty percent display (e.g. 0.105 → "10.5", 0.1 → "10")
  const fmtPct = (r) => {
    const n = r * 100;
    return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
  };

  const canSubmit = name.trim() && phone.trim() && items.length > 0 && cardApi && !cardError && settings?.orders_open !== false;

  // Shared payment-submission path. Works with a card `source_id`, an Apple
  // Pay `source_id`, or a Google Pay `source_id` — they all get the same
  // treatment on the backend / Square Payments API.
  const submitOrderWithSourceId = async (source_id) => {
    if (submitting) return;
    setSubmitting(true);
    setDeclineError(null);
    try {
      const { data } = await api.post("/orders/pay", {
        customer_name: name,
        phone,
        pickup_time: slot,
        notes: notes.trim() ? `ALLERGY: ${notes.trim()}` : "",
        source_id,
        reward: reward?.key || null,
        reward_choice: reward?.choice || null,
        free_drink: freeDrink || null,
        tip: tipAmount,
        pickup_method: pickupMethod,
        vehicle: pickupMethod === "curbside" ? vehicle.trim() : "",
        items: items.map(({ item_id, name, price, quantity, modifications }) => ({
          item_id,
          name,
          price,
          quantity,
          modifications: modifications || null,
        })),
      });
      clear();
      if (data.track_token && data.order_number) {
        localStorage.setItem(`bk_track_${data.order_number}`, data.track_token);
      }
      if (phone) localStorage.setItem("bk_loyalty_phone", phone.replace(/\D/g, ""));
      if (name) localStorage.setItem("bk_loyalty_name", name);
      toast.success("Payment approved · order sent to kitchen");
      navigate(`/order/${data.order_number}`, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Payment failed";
      setDeclineError(msg);
      toast.error(msg, { duration: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (!name.trim() || !phone.trim()) {
      toast.error("Please enter your name and phone before paying");
      return;
    }
    try {
      const source_id = await cardApi.tokenize(total, {
        name: name.trim(),
        phone: phone.trim(),
      });
      await submitOrderWithSourceId(source_id);
    } catch (err) {
      const msg = err?.message || "Payment failed";
      setDeclineError(msg);
      toast.error(msg, { duration: 6000 });
    }
  };

  const handleWalletTokenized = async (source_id, walletName) => {
    if (!name.trim() || !phone.trim()) {
      toast.error(`Please enter your name and phone before using ${walletName}`);
      return;
    }
    if (settings?.orders_open === false) {
      toast.error(
        settings?.orders_closed_message || "Online ordering is paused right now."
      );
      return;
    }
    await submitOrderWithSourceId(source_id);
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-stone-100 flex justify-center">
        <div className="w-full max-w-md lg:max-w-2xl min-h-screen bg-[#FDFBF7] phone-shell rustic-paper flex flex-col items-center justify-center px-6 text-center">
          <div className="font-hand text-4xl text-stone-500 mb-2">
            <T k="checkout.empty_title" d="Cart is empty" />
          </div>
          <p className="text-sm text-stone-500 mb-6">
            <T k="checkout.empty_sub" d="Add something delicious before checking out." />
          </p>
          <Link
            to="/"
            data-testid="back-to-menu-link"
            className="bg-brand-red text-white px-6 py-2.5 rounded-full font-bold uppercase text-xs tracking-widest hover:bg-red-800"
          >
            <T k="checkout.back_to_menu" d="Back to menu" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-stone-100 flex justify-center overflow-x-clip">
      <div className="w-full max-w-md lg:max-w-2xl min-h-[100dvh] bg-[#FDFBF7] phone-shell rustic-paper overflow-x-clip">
        <div className="chalkboard-band px-4 py-3 flex items-center gap-2 border-b-4 border-brand-red">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-1 rounded-full hover:bg-white/10 min-w-[40px] min-h-[40px] flex items-center justify-center"
            data-testid="back-btn"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5 text-brand-yellow" />
          </button>
          <div className="font-display text-brand-yellow text-xl">
            <T k="checkout.title" d="Checkout" />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-5 pb-0">
          {settings?.orders_open === false && (
            <div
              role="alert"
              data-testid="checkout-closed-banner"
              className="bg-brand-red text-white rounded-xl p-4 text-center border-2 border-brand-yellow"
            >
              <div className="font-hand text-brand-yellow text-2xl leading-tight">
                <T k="banner.paused_headline" d="Online ordering is paused" />
              </div>
              <div className="text-xs text-white/90 mt-1">
                <T field="orders_closed_message" />
              </div>
            </div>
          )}

          <section className="bg-white rounded-xl border border-stone-200 p-4">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">
              Order Summary
            </div>
            <ul className="space-y-1.5 text-sm">
              {items.map((i) => (
                <li key={i.line_id || i.item_id} className="flex justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-stone-700">
                      <span className="font-bold">{i.quantity}×</span> {i.name}
                    </span>
                    {i.modifications && (
                      <div className="text-[11px] text-stone-500 italic leading-tight">
                        {i.modifications}
                      </div>
                    )}
                  </div>
                  <span className="text-stone-800 font-bold whitespace-nowrap">
                    ${(i.price * i.quantity).toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="border-t border-stone-200 mt-3 pt-3 space-y-1.5">
              <div className="flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-widest text-stone-500">
                  <T k="common.subtotal" d="Subtotal" />
                </span>
                <span
                  className="text-stone-800 font-bold text-base"
                  data-testid="checkout-subtotal"
                >
                  ${subtotal.toFixed(2)}
                </span>
              </div>
              {promoOff > 0 && (
                <div className="flex justify-between items-baseline" data-testid="checkout-promo-line">
                  <span className="text-xs uppercase tracking-widest text-emerald-700 font-bold">
                    {promo.name} · {Number(promo.percent) % 1 === 0 ? promo.percent : Number(promo.percent).toFixed(1)}% off
                  </span>
                  <span className="text-emerald-700 font-black text-base" data-testid="checkout-promo-discount">−${promoOff.toFixed(2)}</span>
                </div>
              )}
              {rewardDiscount > 0 && (
                <div
                  className="flex justify-between items-baseline"
                  data-testid="checkout-reward-line"
                >
                  <span className="text-xs uppercase tracking-widest text-emerald-700 font-bold">
                    Loyalty · {reward?.choice}
                  </span>
                  <span
                    className="text-emerald-700 font-black text-base"
                    data-testid="checkout-reward-discount"
                  >
                    −${rewardDiscount.toFixed(2)}
                  </span>
                </div>
              )}
              {tax > 0 && (
                <div className="flex justify-between items-baseline">
                  <span className="text-xs uppercase tracking-widest text-stone-500">
                    <T k="checkout.tax_label" d="Sales Tax" /> {fmtPct(salesTaxRate)}%
                  </span>
                  <span
                    className="text-stone-800 font-bold text-base"
                    data-testid="checkout-tax"
                  >
                    ${tax.toFixed(2)}
                  </span>
                </div>
              )}
              {tipAmount > 0 && (
                <div
                  className="flex justify-between items-baseline"
                  data-testid="checkout-tip-line"
                >
                  <span className="text-xs uppercase tracking-widest text-stone-500">
                    <T k="checkout.tip_label" d="Tip" />
                  </span>
                  <span
                    className="text-stone-800 font-bold text-base"
                    data-testid="checkout-tip"
                  >
                    ${tipAmount.toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-baseline border-t border-stone-200 pt-2 mt-1">
                <span className="text-xs uppercase tracking-widest text-stone-500 font-bold">
                  <T k="common.total" d="Total" />
                </span>
                <span
                  className="font-display text-brand-red text-2xl"
                  data-testid="checkout-total"
                >
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                <T k="checkout.name_label" d="Your name" />
              </span>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Farmer"
                autoComplete="name"
                data-testid="checkout-form-name"
                className="mt-1 w-full px-3 py-3 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-base"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                <T k="checkout.phone_label" d="Phone number" />
              </span>
              <input
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="4795551234 or (479) 555-1234"
                inputMode="tel"
                autoComplete="tel"
                data-testid="checkout-form-phone"
                className="mt-1 w-full px-3 py-3 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-base"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                <T k="checkout.pickup_label" d="Pickup time" />
              </span>
              <select
                value={slot}
                onChange={(e) => setSlot(e.target.value)}
                data-testid="checkout-time-slot"
                className="mt-1 w-full px-3 py-3 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-base"
              >
                {TIME_SLOTS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <div>
              {!showAllergy ? (
                <button
                  type="button"
                  onClick={() => setShowAllergy(true)}
                  data-testid="checkout-allergy-btn"
                  className="w-full flex items-center justify-center gap-2 min-h-[44px] rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 text-amber-900 text-sm font-bold hover:bg-amber-100 active:scale-[0.99] transition-all"
                >
                  <AlertTriangle className="w-4 h-4" />
                  Food allergy? Tap to tell the kitchen
                </button>
              ) : (
                <label className="block" data-testid="checkout-allergy-box">
                  <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-800 font-bold">
                    <AlertTriangle className="w-3.5 h-3.5" /> Allergy note for the kitchen
                  </span>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    autoFocus
                    maxLength={300}
                    placeholder="e.g. peanut allergy — please change gloves"
                    data-testid="checkout-form-notes"
                    className="mt-1 w-full px-3 py-2.5 rounded-lg border-2 border-amber-400 bg-amber-50 outline-none focus:border-amber-600 text-base resize-none"
                  />
                  <button
                    type="button"
                    onClick={() => { setNotes(""); setShowAllergy(false); }}
                    data-testid="checkout-allergy-remove"
                    className="mt-1 text-[11px] text-stone-500 underline"
                  >
                    Never mind, no allergy
                  </button>
                </label>
              )}
            </div>
          </section>

          <section className="rounded-3xl border-2 border-stone-200 bg-white p-4 space-y-3" data-testid="pickup-method-section">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">How will you pick up?</div>
            <div className="grid grid-cols-2 gap-2">
              {[["truck", "At the truck", "Walk up to the window when it's ready"], ["curbside", "Curbside", "Stay in your car — we'll bring it out"]].map(([k, t, d]) => (
                <button key={k} type="button" onClick={() => setPickupMethod(k)} data-testid={`pickup-method-${k}`}
                  className={`text-left rounded-2xl border-2 p-3 transition-colors ${pickupMethod === k ? "border-brand-red bg-red-50" : "border-stone-200 bg-white"}`}>
                  <div className={`font-black text-sm ${pickupMethod === k ? "text-brand-red" : "text-stone-800"}`}>{k === "curbside" ? "🚗 " : "🛒 "}{t}</div>
                  <div className="text-[11px] text-stone-500 mt-0.5 leading-snug">{d}</div>
                </button>
              ))}
            </div>
            {pickupMethod === "curbside" && (
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">What does your car look like?</span>
                <input value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="e.g. Blue Honda Civic" maxLength={80} data-testid="checkout-vehicle-input"
                  className="mt-1 w-full px-3 py-2.5 rounded-lg border border-stone-300 bg-white outline-none focus:border-brand-red text-base" />
                <span className="text-[11px] text-stone-500">You'll tap <b>"I'm here"</b> on the tracking page when you arrive.</span>
              </label>
            )}
          </section>

          {settings.drinks_are_free_now && (
            <FreeDrinkPicker value={freeDrink} onChange={setFreeDrink} />
          )}

          <TipPicker subtotal={netSubtotal} value={tipAmount} onChange={setTip} />

          <LoyaltyPanel phone={phone} value={reward} onChange={setReward} />

          <section className="bg-white rounded-xl border border-stone-200 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-brand-red" />
              <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                <T k="checkout.payment_label" d="Payment · Card" />
              </span>
              <span className="ml-auto flex items-center gap-1 text-[10px] text-stone-400">
                <Lock className="w-3 h-3" /> <T k="checkout.secure_label" d="Secure via Square" />
              </span>
            </div>

            <div className="mb-4">
              <SquareWalletButtons
                total={total}
                disabled={submitting || settings?.orders_open === false}
                onTokenized={handleWalletTokenized}
                onError={(msg) => {
                  setDeclineError(msg);
                  toast.error(msg, { duration: 6000 });
                }}
              />
            </div>

            <SquareCardForm onReady={setCardApi} onError={setCardError} />
          </section>

          {declineError && (
            <div
              role="alert"
              aria-live="assertive"
              data-testid="checkout-decline-banner"
              className="bg-red-50 border-2 border-red-500 rounded-xl p-4 text-sm text-red-900 leading-relaxed"
            >
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center shrink-0 mt-0.5 text-[11px] font-black">
                  !
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black uppercase tracking-widest text-[10px] mb-1 text-red-800">
                    <T k="checkout.declined_label" d="Payment declined" />
                  </div>
                  <div className="whitespace-pre-line">{declineError}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setDeclineError(null)}
                  aria-label="Dismiss"
                  className="text-red-700 hover:text-red-900 -mr-1 -mt-1 p-1 leading-none text-lg"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          <div className="bg-brand-yellow/20 border border-brand-yellow/50 rounded-lg p-3 text-xs text-stone-700">
            <T field="checkout_footer" />
          </div>
          <div className="text-center text-[10px] text-stone-400">
            Build: v17 + status-videos
          </div>

          <div
            data-testid="checkout-sticky-bar"
            className="sticky bottom-0 -mx-4 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-[#FDFBF7]/95 backdrop-blur-md border-t border-stone-200"
          >
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              data-testid="checkout-submit-btn"
              className="w-full bg-brand-red text-white py-3.5 rounded-full font-black uppercase tracking-widest text-sm shadow-lg hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
            >
              {submitting ? (
                <T k="checkout.processing" d="Processing payment..." />
              ) : (
                <>
                  <T k="checkout.pay_btn" d="Pay & Order" /> · ${total.toFixed(2)}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
